import React, { useEffect, useState } from 'react';
import { Modal, Form, Select, Radio, Button, Space, Input, message } from 'antd';
import { createTerminalTicket, TerminalTicketResponse } from '../api/terminal';
import { handleApiError } from '../utils/errorHandler';

const TTL_OPTIONS = [
  { label: '30分钟', value: 1800 },
  { label: '1小时', value: 3600 },
  { label: '2小时', value: 7200 },
  { label: '3小时', value: 10800 },
  { label: '6小时', value: 21600 },
  { label: '12小时', value: 43200 },
  { label: '24小时', value: 86400 },
];

export interface GenerateExternalLinkModalProps {
  open: boolean;
  deviceId: string | null;
  onClose: () => void;
}

const pickUrl = (data: TerminalTicketResponse, kind: 'ssh' | 'web') =>
  kind === 'ssh' ? data.ssh_url : data.web_url;

/**
 * 复制到剪贴板：优先 Clipboard API（需 HTTPS 或 localhost），失败时用 execCommand 降级（HTTP 站点常见）。
 */
async function copyTextToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* 非安全上下文、权限被拒等，走降级 */
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', 'readonly');
    ta.style.cssText = 'position:fixed;left:-9999px;top:0;opacity:0;';
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    ta.setSelectionRange(0, text.length);
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

/**
 * 生成外部远程链接：选有效期与 SSH/WEB，调 POST /api/terminal/tickets 后复制或新标签打开。
 */
const GenerateExternalLinkModal: React.FC<GenerateExternalLinkModalProps> = ({
  open,
  deviceId,
  onClose,
}) => {
  const [form] = Form.useForm<{ ttlSeconds: number; connectionType: 'ssh' | 'web' }>();
  const [submitting, setSubmitting] = useState<'copy' | 'open' | null>(null);

  useEffect(() => {
    if (open && deviceId) {
      form.setFieldsValue({
        ttlSeconds: 1800,
        connectionType: 'ssh',
      });
    }
  }, [open, deviceId, form]);

  const run = async (mode: 'copy' | 'open') => {
    if (!deviceId) return;
    try {
      const values = await form.validateFields();
      setSubmitting(mode);
      const { data } = await createTerminalTicket({
        device_id: deviceId,
        ttlSeconds: values.ttlSeconds,
      });
      const url = pickUrl(data, values.connectionType);
      if (mode === 'copy') {
        const ok = await copyTextToClipboard(url);
        if (ok) {
          message.success('链接已复制到剪贴板');
        } else {
          message.error('复制失败，请使用「生成并跳转」或改用 HTTPS 访问管理页');
        }
      } else {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    } catch (e) {
      if ((e as { errorFields?: unknown })?.errorFields) return;
      handleApiError(e, '生成外部链接失败');
    } finally {
      setSubmitting(null);
    }
  };

  return (
    <Modal
      title={`生成远程连接${deviceId ? ` - ${deviceId}` : ''}`}
      open={open && !!deviceId}
      onCancel={onClose}
      footer={null}
      destroyOnClose
      maskClosable={false}
    >
      <Form form={form} layout="vertical" preserve={false}>
        <Form.Item label="设备名称">
          <Input readOnly value={deviceId || ''} style={{ background: '#fafafa' }} />
        </Form.Item>
        <Form.Item
          label="有效期"
          name="ttlSeconds"
          rules={[{ required: true, message: '请选择有效期' }]}
        >
          <Select options={TTL_OPTIONS} placeholder="请选择有效期" allowClear={false} />
        </Form.Item>
        <Form.Item
          label="远程连接类型"
          name="connectionType"
          rules={[{ required: true, message: '请选择连接类型' }]}
        >
          <Radio.Group>
            <Radio value="ssh">SSH</Radio>
            <Radio value="web">WEB</Radio>
          </Radio.Group>
        </Form.Item>
        <Space style={{ width: '100%', justifyContent: 'flex-end', marginTop: 8 }}>
          <Button
            loading={submitting === 'open'}
            disabled={!!submitting && submitting !== 'open'}
            onClick={() => void run('open')}
          >
            生成并跳转
          </Button>
          <Button
            type="primary"
            loading={submitting === 'copy'}
            disabled={!!submitting && submitting !== 'copy'}
            onClick={() => void run('copy')}
          >
            生成并复制
          </Button>
        </Space>
      </Form>
    </Modal>
  );
};

export default GenerateExternalLinkModal;
